import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Shopify's image_url ladder. product-media.liquid asks for width: 3840 for the
 * zoom image, which on a phone means downloading a 2-3 MB file over mobile data
 * before anything appears in the fullscreen dialog. That is why fullscreen looked
 * broken on phones: nothing is shown until the whole file lands.
 * Cap the request at what the screen can actually resolve, with headroom to pinch.
 * Found 2026-09-21.
 */
const IMAGE_WIDTH_LADDER = [832, 1200, 1600, 1920, 2560, 3840];
const ZOOM_HEADROOM = 2; // allows pinch-zoom past 1:1 before it softens
const DPR_CAP = 2; // beyond 2x the extra bytes buy almost nothing on a print

/**
 * Rewrites a Shopify CDN image URL to the smallest ladder width this device can use.
 * @param {string} url - The image URL, expected to carry a `width` query param.
 * @returns {string} The URL with an appropriate width, or the input if not rewritable.
 */
function cappedResolutionUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.searchParams.has('width')) return url;

    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    const cssWidth = Math.max(window.screen?.width || 0, window.innerWidth || 0);
    const needed = cssWidth * dpr * ZOOM_HEADROOM;

    const requested = Number(parsed.searchParams.get('width')) || IMAGE_WIDTH_LADDER.at(-1);
    const target = IMAGE_WIDTH_LADDER.find((w) => w >= needed) ?? IMAGE_WIDTH_LADDER.at(-1);
    if (!target || target >= requested) return url;

    parsed.searchParams.set('width', String(target));
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * True when two image URLs name the same file at the same width (protocol-relative and absolute forms match).
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function sameImageUrl(a, b) {
  try {
    const x = new URL(a, window.location.origin);
    const y = new URL(b, window.location.origin);
    return x.pathname === y.pathname && x.searchParams.get('width') === y.searchParams.get('width');
  } catch {
    return false;
  }
}

/** The fade running on each element, so a close can take over from an open still in progress. */
/** @type {WeakMap<HTMLElement, Animation>} */
const fades = new WeakMap();

/**
 * HA 2026-10-12: on phones the open and close transitions are a short opacity fade on the dialog. The view
 * transition used before snapshots the whole page and recalculates style for the whole document twice (about
 * 100 ms of main thread at 4x CPU) and dropped every frame while it ran, for what on a phone looks like a fade.
 * A fade still running on the element is stopped first, and the new one starts from the opacity on screen, so a
 * quick close after an open never flashes.
 * @param {HTMLElement} element
 * @param {number} to
 * @param {number} duration
 * @returns {Promise<void>}
 */
function fade(element, to, duration) {
  const running = fades.get(element);
  let from = to > 0 ? 0 : 1;
  if (running) {
    from = Number(getComputedStyle(element).opacity);
    running.cancel();
    fades.delete(element);
  }
  if (prefersReducedMotion() || typeof element.animate !== 'function') return Promise.resolve();
  const animation = element.animate([{ opacity: from }, { opacity: to }], {
    duration,
    easing: to > from ? 'ease-out' : 'ease-in',
  });
  fades.set(element, animation);
  return animation.finished.then(
    () => {
      if (fades.get(element) === animation) fades.delete(element);
    },
    () => undefined
  );
}

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** True while the phone close fade runs, so a second tap does not start another. */
  #closing = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (isMobileBreakpoint()) {
      open();
      fade(dialog, 1, 180);
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const declaredUrl = image.getAttribute('data_max_resolution');
    if (!declaredUrl) return false;

    const highResolutionUrl = cappedResolutionUrl(declaredUrl);
    if (this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    // HA 2026-10-12: the srcset now tops out at the photo's own width, so the image on screen is often already
    // the full file. Fetching it again under another URL and swapping it in bought nothing.
    if (image.complete && image.currentSrc && sameImageUrl(image.currentSrc, highResolutionUrl)) {
      this.#highResImagesLoaded.add(highResolutionUrl);
      return false;
    }

    // Marked at the start, so a scroll, a thumbnail and a hover asking at once fetch it once.
    this.#highResImagesLoaded.add(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.decoding = 'async';
    newImage.setAttribute('data_max_resolution', declaredUrl);
    newImage.setAttribute('ref', 'image');
    for (const name of ['style', 'width', 'height']) {
      const value = image.getAttribute(name);
      if (value) newImage.setAttribute(name, value);
    }

    // Swap only once the bigger file is decoded (off the main thread), so the photo never blinks blank between
    // the two. A dropped request keeps the gallery-sized image and allows a later retry.
    newImage.src = highResolutionUrl;
    newImage.decode().then(
      () => {
        if (image.isConnected) image.replaceWith(newImage);
      },
      () => {
        this.#highResImagesLoaded.delete(highResolutionUrl);
      }
    );
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (isMobileBreakpoint()) {
      if (this.#closing) return;
      this.#closing = true;
      try {
        await fade(dialog, 0, 140);
        this.closeDialog();
      } finally {
        this.#closing = false;
      }
      return;
    }

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
